import React, { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { motion } from 'motion/react'
import {
  ArrowLeft, Check, Copy, LogOut, Megaphone, RefreshCw,
  SearchX, Settings, Share2, Trash2, UserPlus, Users
} from 'lucide-react'
import toast from 'react-hot-toast'
import { useTeamStore } from '../store/useTeamStore'
import TeamHeader from '../components/TeamHeader'
import RecentLeftTeams from '../components/RecentLeftTeams'
import TeamMembersList from '../components/TeamMembersList'
import TeamChat from '../components/TeamChat'
import TeamApplicationsList from '../components/TeamApplicationsList'
import TeamDeleteNotice from '../components/TeamDeleteNotice'
import TeamApplyModal from '../components/TeamApplyModal'
import ConfirmModal from '../../../components/ConfirmModal'
import { canEditTeam, teamErrorText } from '../utils/teamFormat'
import { accentColor, cardPrimaryColor, contentBgColor, teamPageBackground } from '../utils/teamColors'
import { CARD_CLASS, SECTION_VARIANT, SECTIONS_VARIANT } from '../utils/teamUi'
import useAppStore from '../../../store/useAppStore'
import useAppEnvironment from '../../../hooks/useAppEnvironment'
import useAudioManager from '../../../hooks/useAudioManager'
import { hapticImpact, hapticNotify } from '../../../utils/haptic'
import { sendYandexGoal } from '../../../services/yandexMetrikaService'

const TeamSkeleton = () => (
  <div className="mx-auto w-full space-y-5 px-4 py-6 lg:max-w-5xl lg:px-6 lg:py-8">
    <div className={`${CARD_CLASS} h-64 animate-pulse`} />
    <div className={`${CARD_CLASS} h-24 animate-pulse`} />
    <div className={`${CARD_CLASS} h-64 animate-pulse`} />
  </div>
)

/**
 * Карточка банды: `/teams/my` — своя (GET /api/team/my/), `/teams/:slug` — любая.
 * Формат ответа одинаковый, скрытые и недоступные блоки приходят как `null`.
 * Распущенная банда отдаёт 404, а slug закреплён навсегда — старая ссылка
 * никогда не приведёт в чужую банду.
 * Слева в шапке — кнопка «назад» (история роутера, фолбэк — каталог банд):
 * на карточку часто приходят из бейджа `TeamTag` посреди игровой ленты.
 */
const TeamPage = () => {
  const { slug } = useParams()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { isTelegramMiniApp, isWebBrowser } = useAppEnvironment()
  const { playSound } = useAudioManager({ select: 'select' })
  const user = useAppStore((state) => state.user)
  const isGuest = useAppStore((state) => state.isGuest)
  const openLogin = useAppStore((state) => state.openLogin)
  const {
    team, isTeamLoading, teamError, isVotePending, isMutating,
    myApplications, loadTeam, loadMyApplications, setVote, shareTeam, leaveTeam, startDelete, resetTeam
  } = useTeamStore()

  const [isApplyOpen, setIsApplyOpen] = useState(false)
  const [isLeaveOpen, setIsLeaveOpen] = useState(false)
  const [isDeleteOpen, setIsDeleteOpen] = useState(false)

  useEffect(() => {
    loadTeam(slug)
    return () => resetTeam()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, isGuest])

  // Свои заявки нужны только тем, кто пока не в банде — по ним видно, подана ли заявка в эту банду
  useEffect(() => {
    if (isGuest) useTeamStore.setState({ myApplications: [] })
    else if (!user?.team) loadMyApplications()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.team, isGuest])

  const handleVote = async (value) => {
    if (isGuest) return openLogin()
    if (!team) return
    const res = await setVote(team.id, value)
    if (res?.ok) {
      hapticNotify('success')
      sendYandexGoal('team-vote')
    } else if (res) {
      toast.error(teamErrorText(res.error, t, res))
    }
  }

  // Назад по истории роутера; если истории нет (вход по диплинку team_<slug>
  // или по прямой ссылке) — в каталог банд
  const handleBack = () => {
    playSound('select')
    hapticImpact('light')
    if (window.history.length > 1) {
      navigate(-1)
    } else {
      navigate('/teams')
    }
  }

  const handleCopyLink = async () => {
    if (!team?.id) return
    hapticImpact('light')
    const bot = import.meta.env.VITE_TELEGRAM_BOT || 'skinozbot'
    const link = isWebBrowser
      ? `${window.location.origin}/teams/${team.id}`
      : `https://t.me/${bot}/app?startapp=team_${team.id}`
    try {
      await navigator.clipboard.writeText(link)
      toast.success(t('referrals.linkCopied', 'Ссылка скопирована!'))
      sendYandexGoal('team-copy-link')
    } catch (e) {
      toast.error(t('publicProfile.shareError', 'Не получилось, попробуй ещё раз'))
    }
  }

  /**
   * «Пригласить в банду»: prepared message готовит бэкенд, id сразу уходит в
   * нативное окно выбора чата (id живёт ограниченное время — не кешируем).
   * 403 / 409 означают, что карточка на экране устарела (набор выключили с
   * другого устройства, приняли последнего участника) — перечитываем банду,
   * вместе с `can_share: false` кнопка пропадёт.
   */
  const handleShare = async () => {
    if (isGuest) return openLogin()
    hapticImpact('medium')
    const res = await shareTeam()
    const messageId = res?.data?.prepared_message?.id

    if (messageId) {
      sendYandexGoal('team-share')
      window.Telegram.WebApp.shareMessage(messageId, () => {})
      return
    }

    if (res?.status === 403 || res?.status === 409) {
      toast.error(teamErrorText(res.error, t, res))
      loadTeam(slug)
      return
    }
    toast.error(t('publicProfile.shareError', 'Не получилось, попробуй ещё раз'))
  }

  const handleLeave = async () => {
    const res = await leaveTeam()
    if (res?.ok) {
      hapticNotify('success')
      sendYandexGoal('team-leave')
      setIsLeaveOpen(false)
      toast.success(t('team.left', 'Ты вышел из банды'))
      navigate('/teams', { replace: true })
    } else if (res) {
      toast.error(teamErrorText(res.error, t, res))
    }
  }

  // Две концовки одного запроса: пустую банду бэкенд распускает сразу
  // (disbanded), иначе запускает таймер на 6 часов и отдаёт живую карточку
  const handleDelete = async () => {
    const res = await startDelete()
    if (res?.ok) {
      hapticNotify('warning')
      setIsDeleteOpen(false)
      if (res.disbanded) {
        sendYandexGoal('team-delete-instant')
        toast.success(t('team.disbanded', 'Банда распущена'))
        navigate('/teams', { replace: true })
        return
      }
      sendYandexGoal('team-delete-start')
      toast.success(t('team.deleteStarted', 'Удаление запущено — 6 часов на отмену'))
    } else if (res) {
      toast.error(teamErrorText(res.error, t, res))
    }
  }

  if (isTeamLoading) return <TeamSkeleton />

  if (teamError === 'not_found') {
    return (
      <div className="mx-auto flex w-full max-w-md flex-col items-center px-4 py-16 text-center">
        <span className="mb-4 flex h-20 w-20 items-center justify-center rounded-full border border-white/10 bg-surface">
          <SearchX size={36} className="text-text-secondary" />
        </span>
        <h1 className="mb-2 text-xl font-bold not-italic text-white">
          {t('team.notFound', 'Банда не найдена')}
        </h1>
        <p className="mb-6 text-sm text-text-secondary">
          {t('team.notFoundDescription', 'Возможно, её распустили или ссылка устарела')}
        </p>
        <button
          onClick={() => navigate('/teams')}
          className="rounded-xl border border-accent-primary/30 bg-accent-primary/10 px-6 py-3 font-medium text-accent-primary transition-all active:scale-[0.98]"
        >
          {t('team.toCatalog', 'В каталог банд')}
        </button>
      </div>
    )
  }

  if (teamError) {
    return (
      <div className="mx-auto flex w-full max-w-md flex-col items-center px-4 py-16 text-center">
        <span className="mb-4 flex h-20 w-20 items-center justify-center rounded-full border border-white/10 bg-surface">
          <RefreshCw size={36} className="text-text-secondary" />
        </span>
        <h1 className="mb-4 text-xl font-bold not-italic text-white">
          {teamError === 'rate_limited' ? t('api.rateLimited') : t('team.loadError', 'Не удалось загрузить банду')}
        </h1>
        <button
          onClick={() => loadTeam(slug)}
          className="rounded-xl bg-gradient-to-r from-accent-primary to-accent-secondary px-6 py-3 font-bold text-dark-primary transition-all active:scale-[0.98]"
        >
          {t('publicProfile.retry', 'Повторить')}
        </button>
      </div>
    )
  }

  // /api/team/my/ отдаёт team: null, когда игрок ни в какой банде не состоит
  if (!team) {
    return (
      <div className="mx-auto flex w-full max-w-md flex-col items-center px-4 py-16 text-center">
        <span className="mb-4 flex h-20 w-20 items-center justify-center rounded-full border border-white/10 bg-surface">
          <Users size={36} className="text-text-secondary" />
        </span>
        <h1 className="mb-2 text-xl font-bold not-italic text-white">
          {t('team.noTeamTitle', 'Ты пока не в банде')}
        </h1>
        <p className="mb-6 text-sm text-text-secondary">
          {t('team.noTeamDescription', 'Вступи в банду из каталога или создай свою — общая казна, своё оформление и командные игры')}
        </p>
        <button
          onClick={() => navigate('/teams')}
          className="gradient-border-cta animate-gradient-shift rounded-xl px-6 py-3 font-bold text-dark-primary transition-all active:scale-[0.98]"
        >
          {t('team.toCatalog', 'В каталог банд')}
        </button>
        <div className="mt-6 w-full text-left"><RecentLeftTeams /></div>
      </div>
    )
  }

  const colors = team.colors
  const accent = accentColor(colors)
  const contentText = cardPrimaryColor(colors)
  const contentBg = contentBgColor(colors)

  const isMember = team.is_member === true
  const myRole = isMember ? team.my_role : null
  // Строго `=== true`: у не-создателя в delete_instant приходит null, а не false
  const isDeleteInstant = team.delete_instant === true
  const isBlocked = team.status !== 'active' // frozen / deleting — внутри банды можно только выйти
  const canApply = !user?.team && !isMember && team.recruiting && team.status === 'active'
  // Приглашение в банду: показываем строго по `can_share` (бэкенд проверяет
  // членство, набор, свободные места и статус) и только в мини-аппе —
  // shareMessage существует лишь там, в вебе ручка ответит 400 telegram_required
  const canShare = team.can_share === true && isTelegramMiniApp &&
    typeof window.Telegram?.WebApp?.shareMessage === 'function'
  // Активная заявка в эту банду уже есть — кнопку подачи меняем на неактивную
  const hasPendingApplication = myApplications.some(
    (app) => app.status === 'pending' && app.team?.id === team.id
  )

  return (
    <div className="lg:px-6 lg:pb-8">
      {/* Полотно оформления банды: на мобильном — во всю ширину экрана, на
          десктопе — карточкой со скруглением, иначе кастомный фон режется
          прямыми углами и выглядит чужеродно на тёмной странице.
          Клипом (overflow) блок не режем: внутри sticky-колонка, а overflow
          на предке ломает прилипание — декоративные слои скругляем сами */}
      <div
        style={{ background: teamPageBackground(colors) }}
        className="relative min-h-[80vh] lg:min-h-0 lg:rounded-3xl lg:shadow-[inset_0_1px_0_rgba(255,255,255,0.07),0_10px_15px_-3px_rgba(0,0,0,0.25),0_4px_6px_-4px_rgba(0,0,0,0.25)]"
      >
        {/* Мягкий свет цветом банды сверху и hairline по кромке — только на
            десктопе, где у полотна есть собственные границы */}
        <div
          className="pointer-events-none absolute inset-x-0 top-0 hidden h-40 rounded-t-3xl lg:block"
          style={{ backgroundImage: `linear-gradient(180deg, ${accent}1F, transparent)` }}
        />
        <div
          className="pointer-events-none absolute inset-x-16 top-0 hidden h-px lg:block"
          style={{ backgroundImage: `linear-gradient(90deg, transparent, ${accent}66, transparent)` }}
        />

        <motion.div
          variants={SECTIONS_VARIANT}
          initial="hidden"
          animate="visible"
          className="relative mx-auto w-full space-y-5 px-4 py-6 lg:max-w-5xl lg:px-6 lg:py-8"
        >
          {/* Шапка экрана: слева «назад», справа действия */}
          <motion.div variants={SECTION_VARIANT} className="flex items-center justify-between">
            <button
              onClick={handleBack}
              className="rounded-xl border border-white/10 bg-surface p-2 text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.05)] transition-all active:scale-[0.98]"
              aria-label={t('common.back', 'Назад')}
            >
              <ArrowLeft size={20} />
            </button>
            <div className="flex items-center gap-2">
              {/* «Пригласить в банду» — иконкой в шапке, как «поделиться» в профиле:
                  строго по can_share и только в мини-аппе */}
              {canShare && (
                <button
                  onClick={handleShare}
                  className="rounded-xl border bg-surface p-2 shadow-[inset_0_1px_0_rgba(255,255,255,0.05)] transition-all active:scale-[0.98]"
                  style={{ borderColor: `${accent}55`, color: accent }}
                  aria-label={t('team.share', 'Пригласить в банду')}
                >
                  <Share2 size={20} />
                </button>
              )}
              <button
                onClick={handleCopyLink}
                className="rounded-xl border border-white/10 bg-surface p-2 text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.05)] transition-all active:scale-[0.98]"
                aria-label={t('publicProfile.copyLink', 'Скопировать ссылку')}
              >
                <Copy size={20} />
              </button>
              {/* Настройки — создателю и заместителю, пока банда активна */}
              {isMember && canEditTeam(myRole) && !isBlocked && (
                <button
                  onClick={() => {
                    playSound('select')
                    navigate('/teams/settings')
                  }}
                  className="rounded-xl border border-white/10 bg-surface p-2 text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.05)] transition-all active:scale-[0.98]"
                  aria-label={t('team.settings', 'Настройки банды')}
                >
                  <Settings size={20} />
                </button>
              )}
            </div>
          </motion.div>

          {/* Заморозка / таймер роспуска — только участникам */}
          {isMember && (
            <motion.div variants={SECTION_VARIANT}>
              <TeamDeleteNotice team={team} />
            </motion.div>
          )}

          <div className="lg:grid lg:grid-cols-12 lg:items-start lg:gap-6">
            <motion.div variants={SECTION_VARIANT} className="lg:col-span-5 lg:sticky lg:top-24">
              <TeamHeader team={team} onVote={handleVote} isVotePending={isVotePending} isGuest={isGuest} />

              {/* Быстрые действия */}
              <div className="mt-3 space-y-2">
                {canApply && (
                  hasPendingApplication ? (
                    <button
                      disabled
                      className="flex w-full items-center justify-center gap-2 rounded-xl border border-white/10 bg-surface p-3.5 font-bold text-text-secondary"
                    >
                      <Check size={18} />
                      {t('team.applicationSent', 'Заявка отправлена')}
                    </button>
                  ) : (
                    <button
                      onClick={() => {
                        hapticImpact('medium')
                        if (isGuest) openLogin()
                        else setIsApplyOpen(true)
                      }}
                      className="flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-accent-primary to-accent-secondary p-3.5 font-bold text-dark-primary transition-all active:scale-[0.98]"
                    >
                      <UserPlus size={18} />
                      {t('team.apply', 'Подать заявку')}
                    </button>
                  )
                )}

                {/* Создателю выход недоступен — сначала передача прав или роспуск */}
                {myRole === 'owner' && !team.delete && (
                  <button
                    onClick={() => setIsDeleteOpen(true)}
                    className="hidden flex w-full items-center justify-center gap-2 rounded-xl border border-red-500/30 bg-red-500/10 p-3 text-sm font-semibold text-red-400 transition-all active:scale-[0.98]"
                  >
                    <Trash2 size={16} />
                    {t('team.delete', 'Распустить банду')}
                  </button>
                )}
              </div>
            </motion.div>

            <div className="mt-5 space-y-5 lg:col-span-7 lg:mt-0">
              {isMember && <motion.div variants={SECTION_VARIANT}><TeamChat key={team.id} team={team} /></motion.div>}
              {/* Объявление лидера — только участникам */}
              {team.announcement && (
                <motion.div
                  variants={SECTION_VARIANT}
                  className={`${CARD_CLASS} p-4`}
                  style={{ borderColor: `${accent}66`, backgroundColor: contentBg }}
                >
                  <h2 className="mb-2 flex items-center gap-2 text-sm font-bold not-italic uppercase tracking-wide" style={{ color: accent }}>
                    <Megaphone size={14} />
                    {t('team.announcement', 'Объявление лидера')}
                  </h2>
                  <p className="whitespace-pre-line break-words text-sm" style={{ color: contentText }}>{team.announcement}</p>
                </motion.div>
              )}

              {/* Описание банды живёт в hero-блоке (TeamHeader) — под названием */}

              {/* Текст набора — для тех, кто ещё не в банде */}
              {!isMember && team.recruit_text && (
                <motion.div
                  variants={SECTION_VARIANT}
                  className={`${CARD_CLASS} p-4`}
                  style={{ backgroundColor: contentBg, borderColor: `${contentText}1F` }}
                >
                  <h2 className="mb-2 text-sm font-bold not-italic uppercase tracking-wide" style={{ color: contentText }}>
                    {t('team.recruitText', 'Кого ищут')}
                  </h2>
                  <p className="whitespace-pre-line break-words text-sm" style={{ color: contentText }}>{team.recruit_text}</p>
                </motion.div>
              )}

              {/* Заявки — модератору и выше (applications: null у остальных) */}
              {Array.isArray(team.applications) && team.applications.length > 0 && (
                <motion.div variants={SECTION_VARIANT}>
                  <TeamApplicationsList team={team} />
                </motion.div>
              )}

              {/* Состав */}
              <motion.div variants={SECTION_VARIANT}>
                <TeamMembersList team={team} />
              </motion.div>

              {/* Выход разрешён даже в заморозке и во время таймера удаления */}
              {isMember && myRole !== 'owner' && (
                <motion.div variants={SECTION_VARIANT}>
                  <button
                    onClick={() => setIsLeaveOpen(true)}
                    className="mx-auto flex w-full max-w-sm items-center justify-center gap-2 rounded-xl border-2 border-white bg-red-700 p-3 text-sm font-semibold text-white shadow-[0_0_0_2px_#0A0A0F] transition-all active:scale-[0.98] lg:max-w-none"
                  >
                    <LogOut size={16} />
                    {t('team.leave', 'Выйти из банды')}
                  </button>
                </motion.div>
              )}
            </div>
          </div>
        </motion.div>
      </div>

      <TeamApplyModal
        team={team}
        isOpen={isApplyOpen}
        onClose={() => setIsApplyOpen(false)}
        onApplied={() => {
          setIsApplyOpen(false)
          loadMyApplications()
        }}
      />

      <ConfirmModal
        isOpen={isLeaveOpen}
        onClose={() => setIsLeaveOpen(false)}
        onConfirm={handleLeave}
        isLoading={isMutating}
        title={t('team.leaveTitle', 'Выйти из банды?')}
        message={t('team.leaveMessage', 'Ты потеряешь доступ к казне и составу. Голос за банду можно будет менять ещё сутки, вступить снова — только по новой заявке.')}
        confirmText={t('team.leave', 'Выйти')}
      />

      <ConfirmModal
        isOpen={isDeleteOpen}
        onClose={() => setIsDeleteOpen(false)}
        onConfirm={handleDelete}
        isLoading={isMutating}
        title={t('team.deleteTitle', 'Распустить банду?')}
        message={isDeleteInstant
          ? t('team.deleteInstantMessage', 'Банда будет распущена сразу — отменить это уже нельзя. Сразу после роспуска можно вступить в другую банду или создать новую.')
          : t('team.deleteMessage', 'Банда проживёт ещё 6 часов и всё это время будет заморожена — за них удаление можно отменить. При роспуске остаток казны уйдёт тебе, а новую банду можно будет создать через час.')}
        confirmText={t('team.deleteConfirm', 'Распустить')}
      />
    </div>
  )
}

export default TeamPage
